/**
 * Base Encounter Claim Schema
 *
 * Contains merged columnNameAliases for fields shared between
 * PharmacyEncounterClaim and MedicalEncounterClaim:
 * - 19 encounter-specific fields (encounter_*)
 * - 5 common fields (date_of_service, ndc, quantity, service_provider_id, wholesaler_invoice_number)
 *
 * Aliases are merged from both schemas to support all vendor CSV formats.
 */
export const baseColumnNameAliases = {
  // Common fields
  date_of_service: [
    "Date Of Service",
    "Fill Date",
    "date_of_service",
    "Dispensed",
    "Fill Sold Date",
    "Service Date",
    "Date of Service (Fill Date)",
    "Rx Fill Date",
    "DOS (Date of Service)",
    "Date Filled",
    "Dispense Date",
  ],
  ndc: [
    "Dispensed Item NDC",
    "Dispensed NDC",
    "Dispensed NDC11",
    "NDC",
    "NDC 11",
    "NDC Number",
    "NDC-11",
    "Product",
    "Product Service ID",
    "Service ID",
  ],
  quantity: [
    "Quantity",
    "Qty Disp",
    "Qnty",
    "Qty Dispensed",
    "Qty",
    "Dispensed Quantity",
    "Dispense Qty",
    "Qty_Dispensed",
    "Billed HCPCS Quantity",
  ],
  service_provider_id: [
    "Service Provider ID",
    "service_provider_id",
    "Service Provider ID NPI",
    "Pharmacy #",
    "Pharmacy DEA",
    "Ph NCPDP",
    "Pharmacy NCPDP",
    "Pharmacy NPI",
    "Store NPI",
    "Service Provider NPI",
  ],
  wholesaler_invoice_number: [
    "Wholesaler Invoice Number",
    "Wholesaler PO",
    "Invoice Number",
    "Wholesaler Invoice #",
    "Invoice #",
    "replenished order number",
  ],

  // Encounter fields (merged from both schemas)
  encounter_billing_service_provider_id: [
    "Encounter Billing Location ID",
    "Billing NPI",
    "billing_provider_id",
    "Billing Provider ID",
    "encounter_billing_service_provider_id",
    "Billing Provider NPI",
    "Encounter Billing Service Provider ID",
  ],
  encounter_date_of_service: [
    "Date of Encounter",
    "Encounter Date",
    "Encounter DOS",
    "encounter_date_of_service",
    "Encounter Date of Service",
  ],
  encounter_rendering_physician_id: [
    "Rendering Physician ID",
    "Physician NPI",
    "Rendering Physician NPI",
    "Rendering Provider NPI",
    "physician_id",
    "encounter_rendering_physician_id",
    "Physician ID",
    "Encounter Physician ID",
    "Encounter Rendering Physician ID",
    "Encounter Rendering Physician NPI",
    "Encounter Rendering Provider NPI",
  ],
  encounter_hcpcs_code: [
    "HCPCS Code",
    "HCPC Code",
    "HCPC CD",
    "J Code",
    "hcpcs_code",
    "encounter_hcpcs_code",
    "Encounter HCPCS Code",
    "Encounter HCPC Code",
    "Encounter HCPC CD",
    "Encounter J Code",
  ],
  encounter_hcpcs_code_modifier_1: [
    "HCPCS Code Modifier 1",
    "HCPCS Modifier 1",
    "J Code Modifier 1",
    "hcpcs_code_modifier_1",
    "encounter_hcpcs_code_modifier_1",
    "Encounter HCPCS Code Modifier 1",
    "Encounter HCPCS Modifier 1",
    "Encounter J Code Modifier 1",
  ],
  encounter_hcpcs_code_modifier_2: [
    "HCPCS Code Modifier 2",
    "HCPCS Modifier 2",
    "J Code Modifier 2",
    "hcpcs_code_modifier_2",
    "encounter_hcpcs_code_modifier_2",
    "Encounter HCPCS Code Modifier 2",
    "Encounter HCPCS Modifier 2",
    "Encounter J Code Modifier 2",
  ],
  encounter_hcpcs_code_modifier_3: [
    "HCPCS Code Modifier 3",
    "HCPCS Modifier 3",
    "J Code Modifier 3",
    "hcpcs_code_modifier_3",
    "encounter_hcpcs_code_modifier_3",
    "Encounter HCPCS Code Modifier 3",
    "Encounter HCPCS Modifier 3",
    "Encounter J Code Modifier 3",
  ],
  encounter_hcpcs_code_modifier_4: [
    "HCPCS Code Modifier 4",
    "HCPCS Modifier 4",
    "J Code Modifier 4",
    "hcpcs_code_modifier_4",
    "encounter_hcpcs_code_modifier_4",
    "Encounter HCPCS Code Modifier 4",
    "Encounter HCPCS Modifier 4",
    "Encounter J Code Modifier 4",
  ],
  encounter_diagnosis_code_primary: [
    "Diagnosis Code 1",
    "Encounter Diagnosis Code 1",
    "ICD-10 Code 1",
    "Primary Diagnosis",
    "Primary Encounter Diagnosis Code",
    "Encounter Diagnosis Code Primary",
  ],
  encounter_diagnosis_code_2: [
    "Diagnosis Code 2",
    "ICD-10 Code 2",
    "diagnosis_code_2",
    "encounter_diagnosis_code_2",
    "Encounter Diagnosis Code 2",
  ],
  encounter_diagnosis_code_3: [
    "Diagnosis Code 3",
    "ICD-10 Code 3",
    "diagnosis_code_3",
    "encounter_diagnosis_code_3",
    "Encounter Diagnosis Code 3",
  ],
  encounter_diagnosis_code_4: [
    "Diagnosis Code 4",
    "ICD-10 Code 4",
    "diagnosis_code_4",
    "encounter_diagnosis_code_4",
    "Encounter Diagnosis Code 4",
  ],
  encounter_diagnosis_code_5: [
    "Diagnosis Code 5",
    "ICD-10 Code 5",
    "diagnosis_code_5",
    "encounter_diagnosis_code_5",
    "Encounter Diagnosis Code 5",
  ],
  encounter_diagnosis_code_6: [
    "Diagnosis Code 6",
    "ICD-10 Code 6",
    "diagnosis_code_6",
    "encounter_diagnosis_code_6",
    "Encounter Diagnosis Code 6",
  ],
  encounter_diagnosis_code_7: [
    "Diagnosis Code 7",
    "ICD-10 Code 7",
    "diagnosis_code_7",
    "encounter_diagnosis_code_7",
    "Encounter Diagnosis Code 7",
  ],
  encounter_diagnosis_code_8: [
    "Diagnosis Code 8",
    "ICD-10 Code 8",
    "diagnosis_code_8",
    "encounter_diagnosis_code_8",
    "Encounter Diagnosis Code 8",
  ],
  encounter_diagnosis_code_9: [
    "Diagnosis Code 9",
    "ICD-10 Code 9",
    "diagnosis_code_9",
    "encounter_diagnosis_code_9",
    "Encounter Diagnosis Code 9",
  ],
  encounter_diagnosis_code_10: [
    "Diagnosis Code 10",
    "ICD-10 Code 10",
    "diagnosis_code_10",
    "encounter_diagnosis_code_10",
    "Encounter Diagnosis Code 10",
  ],
  encounter_place_of_service_code: [
    "Place of Service",
    "Place of Service Code",
    "POS Code",
    "place_of_service_code",
    "encounter_place_of_service_code",
    "Encounter Place of Service Code",
  ],
};
